import { BlockItem } from "@/apis/preview/v2/types";
import { Separator } from "@radix-ui/react-separator";
import { Check, ChevronDown, Info } from "lucide-react";
import { useState } from "react";

type BlockType = "BLOCK" | "DETAIL" | "FOOTNOTE";

const BlockHeader = ({
  blockType,
  isCollapsed,
  onClick,
}: {
  blockType: BlockType;
  isCollapsed: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className="flex h-[36px] px-[16px] py-[8px] items-center self-stretch gap-[4px] cursor-pointer "
      onClick={onClick}
    >
      <ChevronDown
        className={`w-[14px] h-[14px] text-neutral-500 transform transition-transform duration-200 ${
          isCollapsed ? "-rotate-90" : "rotate-0"
        }`}
      />
      <span className="caption-1-semibold text-primary">
        {blockType === "BLOCK" ? "블록 태깅" : "디테일 태깅"}
      </span>
    </div>
  );
};

const BlockValue = ({ value }: { value: string }) => {
  return (
    <div className="flex flex-col items-start gap-[8px] self-stretch mx-[16px] mb-[8px]">
      <div className="flex w-full px-[16px] py-[12px] flex-col justify-center items-start gap-[8px] self-stretch rounded-md bg-neutral-100 ">
        <span className="label-1-normal-medium text-primary">{value}</span>
      </div>
    </div>
  );
};

const BlockTag = ({
  tags,
  blockType,
}: {
  tags: string[];
  blockType: BlockType;
}) => {
  return (
    <div className="flex flex-col items-start gap-[8px] self-stretch mx-[16px] mb-[8px]">
      {tags.length > 0 && (
        <div className="flex flex-row items-center gap-[6px] self-stretch  px-[12px] py-[8px]  ">
          <Check className="w-[14px] h-[14px] p-[3px] items-center justify-center bg-black text-white rounded-full" />
          <span className="label-1-normal-medium text-primary text-ellipsis overflow-hidden whitespace-nowrap">
            {tags[0]}
          </span>
        </div>
      )}
      {blockType === "FOOTNOTE" && (
        <div className="flex px-[12px] py-[8px] items-start justify-start self-stretch gap-[4px] rounded-md bg-[#FAFAFA] ">
          <Info className="w-[16px] h-[16px] items-center justify-center  text-[#737373] rounded-full" />
          <span className="caption-1-medium text-[#737373]">
            해당 영역은 각주입니다. XBRL편집기에서 디테일 태깅을 진행하세요.
          </span>
        </div>
      )}
    </div>
  );
};

const BlockItemComponent = ({
  blockItem,
  blockType,
}: {
  blockItem: BlockItem;
  blockType: BlockType;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="flex flex-col items-start self-stretch">
      <div className="flex flex-col pb-[8px] w-full">
        <BlockHeader
          blockType={blockType}
          isCollapsed={isCollapsed}
          onClick={handleToggle}
        />
        {!isCollapsed && (
          <>
            <BlockValue value={blockItem.value} />
            <BlockTag
              tags={blockItem.tags.map((tag) => tag.koLabel)}
              blockType={blockType}
            />
          </>
        )}
      </div>
      <Separator className="w-full h-[1px] bg-neutral-200" />
    </div>
  );
};

export const TextblockItem = ({ blockItems }: { blockItems: BlockItem[] }) => {
  return (
    <>
      {blockItems.map((item, index) => (
        <BlockItemComponent
          key={index}
          blockItem={item}
          blockType={item.type}
        />
      ))}
    </>
  );
};
